import React, {useEffect, useImperativeHandle, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { addNodeUnderParent, changeNodeAtPath, find, getFlatDataFromTree, getNodeAtPath, insertNode, map, RCCAlert, RCCBadge, RCCButton, RCCCheckBox, RCCDropDownItem, RCCGrid, RCCHierarchicalTree, RCCHookToast, RCCIcon, RCCInput, RCCRefModalProps, RCCServicesApi, RCCSwitch, RCCTooltip, removeNode, removeNodeAtPath, toggleExpandedForAll, walk } from "react-common-components";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import { v4 } from "uuid";
import { PermissionEnum } from "src/context/Auth/interface";
import { useAuth } from "src/context/Auth/context";
import IStandardItens, { IRefStandardItemsModals, IStandardItem,IStandardItemModel } from "./interface";
import OptionsColumn from "../DefaultColumnsList/Options";
import itemSchema from "./schemas";
import OverflowSpan from "./styles";
import CheckPermission from "../CheckPermission";
import Modal from "../Modal";

const StandardItensModal: React.ForwardRefRenderFunction<IRefStandardItemsModals,IStandardItens> = ({
	returnFunction,
	isImport
}, ref) => {
	const { translate} = useTranslation();
	const {addToast} = RCCHookToast();
	const { setLoading } = useLoaderBackdrop();
	const controllerUrl = "Standards/";
	const [itens,setItens] = useState<Array<any>>([]);
	const [deletedItens,setDeletedItens] = useState<Array<number>>([]);
	const [modalTitle,setmodalTitle] = useState<string>();
	const [standardId,setstandardId] = useState<number>();
	const [checkAll, setCheckAll] = useState(false);
	const [expandAll, setExpandAll] = useState(false);
	const [updatingItem, setUpdatingItem] = useState(false);

	const [isAdding, setIsAdding] = useState(false);
	const [clickAdd, setClickAdd] = useState(false);

	const { checkIfHasSpecificPermission } = useAuth();

	const { 
		handleSubmit, 
		register, 
		reset,
		setError, 
		formState: {errors},
		clearErrors 
	} = useForm();
	
	const { 
		handleSubmit:handleSubmitImport, 
		register:registerImport,
		reset:resetImport,
		setValue:setValueImport
	} = useForm();

	const modalItens = useRef<RCCRefModalProps>(null);

	const getNodeKeyFunction =({treeIndex}:any) => treeIndex;

	const requestItens = async(id:number) => {
		setLoading(true);
		try {
			const dataController = await RCCServicesApi.getRequest({
				method: `${controllerUrl}ListItens`,
				urlParameters: {
					ada001Id:id,
					isImport
				}
			});

			setstandardId(id);
			setmodalTitle(dataController.data.ada001Descricao);
			setItens(dataController.data.itens);
			setUpdatingItem(false);
			setExpandAll(false);
			setCheckAll(false);
			setLoading(false);

		} catch (error) {
			standardErrorMessages(error, translate("Erro ao buscar itens"), addToast);
			setLoading(false);
		}
	};

	const actionOpen = async(id:number) => {
		reset();
		resetImport();
		setItens([]);
		setDeletedItens([]);
		await requestItens(id);
		modalItens.current?.openModal();
	};

	useImperativeHandle(ref, () => ({
		actionOpen
	}));

	const importItens = (data:any) => {
		if(!isImport) 
			return;

		if(!data.selectedItens){
			resetImport();
			modalItens.current?.closeModal();
			return;
		}

		const selectedIds = data.selectedItens.filter((element: any) => element);
		const select = [] as IStandardItemModel[];
		const flatData = getFlatDataFromTree({treeData:[...itens],getNodeKey:getNodeKeyFunction,ignoreCollapsed:false});
		
		selectedIds.forEach((id:string) => {
			const itenFound = flatData.find((x:any) => x.node.id === id);
			if(itenFound){
				select.push(itenFound.node as IStandardItemModel);
			}
		});	

		if(returnFunction) returnFunction(select);
		resetImport();
		modalItens.current?.closeModal();
	};

	const submitItem = async (data:any) => {
		
		 try {
			clearErrors();
			const nodepath = JSON.parse(`[${data.path}]`);

			const repeatedCode = find({
				getNodeKey:getNodeKeyFunction, 
				treeData:[...itens],
				searchMethod:(searchData:any) => searchData.node.ada002Codigo.trim() === data.ada002Codigo.trim() && searchData.node.id !== data.id}
			);
			
			await itemSchema(translate).validate(data, {
				abortEarly: false,
				context:{isRepeted:repeatedCode.matches.length > 0}
			});
			
			const modifiedNode = getNodeAtPath({treeData:[...itens],getNodeKey:getNodeKeyFunction,path:nodepath})?.node;

			if(modifiedNode){
				modifiedNode.onChange = false;
				modifiedNode.ada002Codigo = data.ada002Codigo;
				modifiedNode.ada002Descricao = data.ada002Descricao;
				modifiedNode.ada002Ativo = data.ada002Ativo;
				const newTree = changeNodeAtPath({treeData:[...itens],path:nodepath,getNodeKey:getNodeKeyFunction,newNode:modifiedNode});
				setItens(newTree);
			}

			setUpdatingItem(false);
			reset();
			
		} catch (error) {
			if(getValidationErrors(error, setError))
				return;
			standardErrorMessages(error, translate("Erro ao gerar item de norma"), addToast);
		}
	};

	const setAllItensChecked = () => {
		if(!isImport) 
			return;

		walk({
			treeData:[...itens],
			getNodeKey:getNodeKeyFunction,
			ignoreCollapsed:false,
			callback:(info: any) => {
				setValueImport(`selectedItens.${info.treeIndex}`,!checkAll ? info.node.id : false);
			}
		});
		setCheckAll(!checkAll);	
	};

	const addNewItem = (parentKey?:number|string,idPai?:number) => {
		if(isImport) 
			return;

		if(updatingItem){
			toastMessage("adicionar");
			return;
		}

		reset();
		
		setUpdatingItem(true);
		setIsAdding(true);
		const newNode = {
			id:v4(),
			ada002Codigo:"",
			ada002Id:0,
			ada002Ordem:1, 
			ada002Ada002Id:idPai,
			ada002Ativo:true,
			onChange:true
		} as IStandardItem;		

		let newTreeData;

		if(!parentKey && parentKey !== 0){
			newTreeData = insertNode({
				treeData:[...itens],
				depth:0,
				minimumTreeIndex:itens.length,
				newNode, 
				getNodeKey:getNodeKeyFunction
			}).treeData;
		}else{
			newTreeData = addNodeUnderParent({
				treeData:[...itens],
				newNode, 
				parentKey, 
				getNodeKey:getNodeKeyFunction,
				expandParent :true
			}).treeData;
		}
		
		setItens(newTreeData);
	};

	const updateItem = (item:any,path:Array<string|number>) => {
		if(isImport) 
			return;
		if(updatingItem){
			toastMessage("atualizar");
			return;
		}

		reset();
		setUpdatingItem(true);

		const newNode = {...item};
		newNode.onChange = true;
		const newTree = changeNodeAtPath({treeData:[...itens], path, getNodeKey:getNodeKeyFunction, newNode});

		setItens(newTree);		
	};

	const removeItem = (path:Array<string|number>) => {
		const data = removeNode({treeData:[...itens],path,getNodeKey:getNodeKeyFunction});
		
		if(data?.node.ada002Id !== 0){
			setDeletedItens((prevState) => [...prevState,data?.node.ada002Id]);
		}
		setItens(data?.treeData||[]);
	};

	const toastMessage = (action:"atualizar"|"adicionar"|"salvar"|"movimentar") => {
		addToast({
			title: translate("Aviso"),
			description: translate(`Confirme sua ultima ação antes de ${action} o item`),
			color: "yellow"
		});
	};

	const showItens = () => {
		const newitens = toggleExpandedForAll({treeData:[...itens],expanded:!expandAll});	
		setItens(newitens);
		setExpandAll(!expandAll);
	};

	const cancelChange = (isAdd:boolean, item:any, path:Array<string|number>) => {
		let newTreeData;
		if(isAdd){
			if(getNodeAtPath({treeData:[...itens],getNodeKey:getNodeKeyFunction,path})?.node)
				newTreeData = removeNodeAtPath({ treeData:[...itens], path , getNodeKey:getNodeKeyFunction});
			setIsAdding(false);	
		}else{
			const newItem = {...item};
			newItem.onChange = false;
			newTreeData = changeNodeAtPath({treeData:[...itens],path,getNodeKey:getNodeKeyFunction,newNode:newItem});
		}

		setUpdatingItem(false);
		setItens(newTreeData);
	};

	const canDrop = (data:any):boolean => {
		if(data?.node?.onChange)
			return false;
		if(updatingItem){
			toastMessage("movimentar");
			return false;
		}
		return data.nextParent?.id === data.prevParent?.id;
	};

	const saveHierarchicalTree = async() => {

		if(updatingItem){
			toastMessage("salvar");
			return;
		}

		const newOrder = map({
			treeData:[...itens],
			getNodeKey:getNodeKeyFunction,
			ignoreCollapsed:false,
			callback:(info: any) => {
				const newNode = {...info.node};
				newNode.ada002Ordem = info.treeIndex+1;
				newNode.ada001Id = standardId;
				return newNode;
			}
		});
		
		try {
			await RCCServicesApi.postRequest({
				method: `${controllerUrl}SubmitTree`,
				body: {
					itens:newOrder,
					deletedItens
				}
			});

			addToast({
				color: "green",
				description: translate("Itens da norma salvos com sucesso"),
				title: translate("Sucesso")
			});
			modalItens.current?.closeModal();

		} catch (error) {
			standardErrorMessages(error, translate("Erro ao salvar itens da norma"), addToast);
		}
	};

	const options = (item:IStandardItem|any,path:Array<string | number>) => (
		<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
			<OptionsColumn position="leftEnd">	
				<RCCDropDownItem
					selfEnclose
					onClick={() => { 	
						setClickAdd(true);
						addNewItem(path[path.length-1],item.ada002Id); 
					}}
				>
					<RCCIcon 
						name="plus" 
					/>
					{translate("Novo item")}
				</RCCDropDownItem>
				<RCCDropDownItem
					selfEnclose
					onClick={() => {
						setClickAdd(false);
						updateItem(item,path);
				 }}
				>
					<RCCIcon 
						name="pencil" 
					/>
					{translate("Alterar")}
				</RCCDropDownItem>
				<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
					<RCCDropDownItem 
						selfEnclose
						onClick={() => {removeItem(path);}}
					>
						<RCCIcon 
							name="trash" 
						/>
						{translate("Excluir")}
					</RCCDropDownItem>														
				</CheckPermission>
			</OptionsColumn>														
		</CheckPermission>
	);

	useEffect(() => {
		if(!clickAdd)
			setIsAdding(false);
	}, [itens]);

	return (
		<Modal
			ref={modalItens}
			header= {{
				title: `${translate(`${isImport ? "Seleção de itens" : "Itens"} da norma`)} - ${modalTitle}`,
				icon: {
					name: "hierarchy"
				}
			}}
			footer= {{
				showSaveButton: isImport,
				buttonLoadingText: translate("Carregando"),
				saveButtonText: translate("Adicionar"),
				cancelButtonText: translate("Cancelar"),
				customModalFooter: !isImport && 
				<>
					<RCCButton 
						size="md" 
						type="button" 
						color="green"
						onClick={saveHierarchicalTree}
					> 
						{translate("Salvar")} 
					</RCCButton>   
				
					<RCCButton 
						size="md" 
						type="button" 
						onClick={() => {modalItens.current?.closeModal();}}
					> 
						{translate("Cancelar")} 
					</RCCButton>
				</>
			}}
			onSubmit = {!isImport ? handleSubmit(submitItem):handleSubmitImport(importItens)}
		>
			{itens.length || !isImport ? (
				<>
					<RCCGrid container>
						<RCCGrid 
							container 
							justifyContent="flex-end"
							style={{ marginTop: "1rem"}}
						>
							<RCCTooltip
								text={expandAll ? translate("Recolher todos") : translate("Expandir todos")}
								position="left"
							>
								<RCCButton 
									onClick = {() => {showItens();}}
									icon = {{
										name: expandAll ? "compress" : "expand"
									}}
									layout="isRounded"
									size="md"
								/>
							</RCCTooltip>
							{isImport ? 
								<RCCTooltip
									text={checkAll ? translate("Desmarcar todos"):translate("Marcar todos")}
									color="purple"
									position="left"
								>
									<RCCButton 
										onClick = {setAllItensChecked}
										icon = {{
											name: checkAll ? "notCheckSquare":"checkSquare"
										}}
										color = "purple"
										size="md"
									/>
								</RCCTooltip>
								:
								<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
									<RCCTooltip
										text={translate("Novo item")}
										color="purple"
										position="left"
									>
										<RCCButton 
											onClick = {() => {
												setClickAdd(true);
												addNewItem();
											}}
											icon = {{
												name: "plus"
											}}
											layout="isRounded"
											size="md"
											color = "purple"
										/>
							
									</RCCTooltip>
								</CheckPermission>	
							}
						</RCCGrid>
						<div 
							style={{ width: "100%", marginBottom: "1rem" }}
						>
							{itens && itens.length > 0 ? (
								<RCCHierarchicalTree
									data={itens}
									styleInline="
										width: 100% !important; 
										margin-top: 1rem;
										.rst__rowTitle{
											font-weight: normal !important;
										}
										.rst__node{
											height: unset !important;
											min-height: 42px !important;
										}
										.rst__tree{ 
											height: 100%;
											padding-bottom: 68px;
										}"
									onChange={(treeData: any) => {
										setIsAdding(false);
										setItens(treeData);
									}}
									canDrop={canDrop}
									canDrag = {!isImport && checkIfHasSpecificPermission("audit/standards", PermissionEnum.CREATE_UPDATE)}
									customNode={({
										node,
										path,
										treeIndex
									}: any) => (
										<RCCGrid container>
											{!node.onChange ? 
												<RCCGrid container>
													<RCCGrid container item 
														xs={8} 
														alignItems="center"
													>
														{isImport ?
															<RCCCheckBox 
																name={`selectedItens.${treeIndex}`} 
																register={registerImport} 
																value={node.id}
																label={`${node.ada002Codigo} - ${node.ada002Descricao}`}
																labelPosition="end"
																styleInline={{label:"color: var(--default-color-font);"}}
															/>
															:
															<OverflowSpan>{node.ada002Codigo} - {node.ada002Descricao}</OverflowSpan>
														}
											
													</RCCGrid>
													{!isImport && 
														<>
															<RCCGrid container item 
																xs={4} 
																justify="flex-end"
															>
																<RCCBadge 
																	color={node.ada002Ativo ? "green" : "red"}
																	styleInline="font-size: 0.83rem; margin-right: 1rem;"
																>
																	{node.ada002Ativo ? translate("Ativo") : translate("Inativo")}
																</RCCBadge>
																{options(node,path)}
														
															</RCCGrid>
														</>	}
												</RCCGrid>
												:
												<RCCGrid container item> 
													<RCCGrid container item 
														wrap="nowrap" 
														spacing={2}
													> 
														<input type="hidden" {...register("id")} name="id" defaultValue={node.id} />
														<input type="hidden" {...register("ada002Id")} name="ada002Id" defaultValue={node.ada002Id} />
														<input type="hidden" {...register("path")} name="path" defaultValue={path} />
														<RCCGrid 
															container 
															item
															xs={4}
														> 
															<RCCInput 
																label={translate("Código")} 
																name="ada002Codigo" 
																defaultValue={node.ada002Codigo} 
																error={errors.ada002Codigo}
																register={register}
																maxLength={20}
															/>
														</RCCGrid>
														<RCCGrid 
															container 
															item
															xs={4}
														> 
															<RCCInput 
																label={translate("Descrição")} 
																name="ada002Descricao" 
																defaultValue={node.ada002Descricao} 
																error={errors.ada002Descricao}
																register={register}
																maxLength={200}
															/>
														</RCCGrid>
														<RCCGrid 
															container 
															item
															xs={2}
														> 
															<RCCSwitch 
																label={translate("Ativo")} 
																name="ada002Ativo" 
																defaultChecked={node.ada002Ativo} 
																error={errors.ada002Ativo}
																register={register}
																style={{ marginRight: "2rem" }}
															/>
														</RCCGrid>
														<RCCGrid 
															container 
															item 
															alignItems="center" 
															justifyContent="flex-end"
															xs={2}
														> 	
															<RCCTooltip
																text={translate("Salvar")}
																color="green"
																position={treeIndex === 0 ? "left":"top"}
															>
																<RCCButton 
																	type="submit" 
																	color="green" 
																	icon={{name:"check"}}
																	size="sm"
																/>
															</RCCTooltip>
															<RCCTooltip
																text={translate("Cancelar")}
																color="red"
																position={treeIndex === 0 ? "bottom":"top"}
															>
																<RCCButton 
																	onClick={() => {
																		cancelChange(isAdding, node, path);						
																	}} 
																	color="red"
																	icon={{name:"close"}}
																	size="sm"
																/>
															</RCCTooltip>
														</RCCGrid>
													</RCCGrid>
												</RCCGrid>
											}
										</RCCGrid>
									)}
								/>
							) : (
								<RCCAlert 
									title={translate("Atenção")}
									description={translate("Não foi encontrado itens para esta norma")}
									color="blue"
								/>
							)}
						
						</div>
					</RCCGrid>
				</>
			) : (
				<RCCAlert 
					title={translate("Atenção")}
					description={translate("Não foi encontrado itens para esta norma")}
					color="blue"
				/>
			)}
		</Modal>
	);
};

export default React.forwardRef(StandardItensModal);
